import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Star,
  LayoutDashboard,
  CheckSquare,
  Gift,
  BookOpen,
  Users,
  Settings,
  ShieldCheck,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { get } from '@/shared/api/api_client';
import { NotificationBell } from '@/widgets/NotificationBell';
import { cn } from '@/shared/utils/cn';
import type { Family } from '@/entities/family';

// ---------------------------------------------------------------------------
// Nav link definitions
// ---------------------------------------------------------------------------
interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const parentLinks: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Chores', to: '/chores', icon: <CheckSquare size={18} /> },
  { label: 'Rewards', to: '/rewards', icon: <Gift size={18} /> },
  { label: 'Ledger', to: '/ledger', icon: <BookOpen size={18} /> },
  { label: 'Family', to: '/family', icon: <Users size={18} /> },
  { label: 'Settings', to: '/settings', icon: <Settings size={18} /> },
];

const childLinks: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Chores', to: '/chores', icon: <CheckSquare size={18} /> },
  { label: 'Rewards', to: '/rewards', icon: <Gift size={18} /> },
];

const adminLink: NavItem = {
  label: 'Admin',
  to: '/admin',
  icon: <ShieldCheck size={18} />,
};

// ---------------------------------------------------------------------------
// Active link class helper
// ---------------------------------------------------------------------------
function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-1.5 px-1 py-0.5 text-sm font-medium transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded',
    isActive
      ? 'text-primary-700 border-b-2 border-primary-600'
      : 'text-slate-600 hover:text-slate-900 border-b-2 border-transparent',
  );
}

function mobileNavLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    isActive
      ? 'bg-primary-50 text-primary-700'
      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
  );
}

// ---------------------------------------------------------------------------
// Navbar component
// ---------------------------------------------------------------------------
export default function Navbar(): React.ReactElement {
  const { appUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const role = appUser?.role ?? 'child';
  const familyId = appUser?.familyId ?? '';
  const isParent = role === 'parent' || role === 'admin';

  const { data: familyData } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => get<{ family: Family }>(`/families/${familyId}`),
    enabled: isParent && !!familyId,
    staleTime: 5 * 60 * 1000,
  });
  const familyCode = familyData?.family?.familyCode;

  let navLinks: NavItem[] = role === 'child' ? childLinks : parentLinks;
  if (role === 'admin') {
    navLinks = [...parentLinks, adminLink];
  }

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  // Close mobile drawer on route change
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Trap focus and close on Escape for mobile drawer
  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6">
          {/* Logo */}
          <NavLink
            to="/dashboard"
            className="shrink-0 flex items-center gap-1.5 text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded"
            aria-label="KidKred — go to dashboard"
          >
            <Star size={22} className="text-accent-500" aria-hidden="true" />
            <span className="text-lg font-bold tracking-tight">KidKred</span>
          </NavLink>

          {/* Desktop nav — flex-1 + justify-center so items never overflow */}
          <nav className="hidden md:flex flex-1 justify-center items-center gap-4" aria-label="Primary navigation">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} className={navLinkClass}>
                <span aria-hidden="true">{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right side */}
          <div className="shrink-0 flex items-center gap-2">
            {familyId && <NotificationBell familyId={familyId} />}

            {/* Family code chip — parents only */}
            {isParent && familyCode && (
              <span
                title="Kids login code"
                className="hidden sm:inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 font-mono text-xs font-bold tracking-widest text-primary-700 select-all"
              >
                {familyCode}
              </span>
            )}

            {/* User display name (desktop) — links to account settings */}
            {appUser && (
              <NavLink
                to="/account"
                className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate hover:text-primary-700 transition-colors"
                title="Account settings"
              >
                {appUser.displayName}
              </NavLink>
            )}

            {/* Sign out (desktop) */}
            <button
              type="button"
              onClick={() => void handleSignOut()}
              className={cn(
                'hidden sm:flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-slate-600',
                'hover:bg-slate-100 hover:text-slate-800',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                'transition-colors duration-150',
              )}
              aria-label="Sign out"
            >
              <LogOut size={16} aria-hidden="true" />
              <span className="hidden md:inline">Sign out</span>
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              className={cn(
                'flex md:hidden h-10 w-10 items-center justify-center rounded-lg text-slate-500',
                'hover:bg-slate-100',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
              )}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => setMobileOpen((prev) => !prev)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
          aria-hidden="true"
          onClick={closeMobile}
        />
      )}

      {/* Mobile slide-out drawer */}
      <div
        id="mobile-nav-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={cn(
          'fixed inset-y-0 right-0 z-40 w-72 bg-white shadow-xl md:hidden',
          'transition-transform duration-300 ease-in-out',
          mobileOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Drawer header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5">
            <div className="flex items-center gap-1.5 text-primary-700">
              <Star size={20} className="text-accent-500" aria-hidden="true" />
              <span className="font-bold tracking-tight">KidKred</span>
            </div>
            <button
              type="button"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg text-slate-500',
                'hover:bg-slate-100',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              )}
              onClick={closeMobile}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* User info — tapping name goes to account settings */}
          {appUser && (
            <NavLink
              to="/account"
              onClick={closeMobile}
              className="block border-b border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold text-sm">
                  {appUser.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{appUser.displayName}</p>
                  <p className="text-xs text-slate-500 capitalize">{role} · Account settings</p>
                  {isParent && familyCode && (
                    <p className="mt-0.5 font-mono text-xs font-bold tracking-widest text-primary-700">
                      {familyCode}
                    </p>
                  )}
                </div>
              </div>
            </NavLink>
          )}

          {/* Nav links */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={mobileNavLinkClass}
                onClick={closeMobile}
              >
                <span aria-hidden="true">{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Sign out */}
          <div className="border-t border-slate-100 px-3 py-4">
            <button
              type="button"
              onClick={() => { closeMobile(); void handleSignOut(); }}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium',
                'text-danger-600 hover:bg-danger-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500',
                'transition-colors duration-150',
              )}
            >
              <LogOut size={18} aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
